import { GoogleSignin } from '@react-native-google-signin/google-signin';
import { useFocusEffect } from '@react-navigation/native';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import { Modal } from 'react-native';
import { useDispatch } from 'react-redux';

import actions from '../../../actions';
import {
    setBackupAttachmentsWifiEnabled,
    setBackupMyDocsEnabled,
    setBackupVisitAttachmentsEnabled,
    setGoogleCloudEnabled,
    setICloudEnabled
} from '../../../actions/prefs';
import {
    Button,
    Page,
    Section,
    showNotification,
    Title,
    TopNotification,
    View
} from '../../../common';
import {
    Log,
    QAMode
} from '../../../common/Log';
import { useBackup } from '../../../hooks/useBackup';
import { COLORS } from '../../../styles/colors';
import { CheckboxItem } from '../../Account/NotificationsSettings';
import { SwitchWithLabel } from '../../Visits/snippets/NewVisitModal/snippets';
import { DebugButton } from '../Debug/DebugScreen';
import {
    backupInfoProcessing,
    toLog
} from './Backup';
import { BackupList } from './BackupList';
import {
    setBackupError,
    setRestoreError,
    useBackupError
} from './common/errors';
import {
    getDriveAccessToken,
    getGoogleDriveBackupInfo,
    restoreGoogleBackup,
    startGoogleBackup
} from './google/googleDrive';
import {
    fetchAllDataFiles,
    fetchMetadataFiles,
    getICloudBackupInfo,
    restoreICloud,
    startICloudBackup
} from './icloud/icloud';
import { finishICloudRestoreAttaches } from './icloud/iCloudRestoreAttaches';
import { CloudBackupControl } from './info/CloudBackupControl';
import { styles } from './styles';

// eslint-disable-next-line max-statements
export const CloudConnectSettings = Page.register(() => {

    //region Variables
    const dispatch = useDispatch();
    const b = useBackup();
    const {
        isInfoLoading,
        connectGoogleDrive,
        isICloudInfoProcessing,
        googleDriveConnected,
        useICloudBackup,
        useGoogleBackup,
        useGoogleRestore,
        backupProcessing,
        setBackupProcessing,
        backupVisitAttachments,
        backupMyDocs,
        backupAttachmentsWifi,
        getGoogleDiskInfo,
        getICloudInfo,
        googleCloudEnabled,
        iCloudEnabled,
        profileId,
        googleLastBackup,
        iCloudLastBackup,
    } = b;

    const [ gc, setGc ] = useState(googleCloudEnabled);
    const [ ic, setIc ] = useState(iCloudEnabled);
    const [ attaches, setAttaches ] = useState(backupVisitAttachments);
    const [ myDocs, setMyDocs ] = useState(backupMyDocs);
    const [ wifi, setWifi ] = useState(backupAttachmentsWifi);
    const [ chooseVisible, setChooseVisible ] = useState(false);
    const [ chooseResult, setChooseResult ] = useState(null);
    const {
        reloadErrors,
        backupError,
        restoreError,
    } = useBackupError();

    //endregion

    //region effects

    useEffect(() => {
        setGc(googleCloudEnabled);
    }, [ googleDriveConnected, googleCloudEnabled ]);

    useEffect(() => {
        setAttaches(backupVisitAttachments);
    }, [ backupVisitAttachments ]);

    useEffect(() => {
        setMyDocs(backupMyDocs);
    }, [ backupMyDocs ]);

    useEffect(() => {
        setWifi(backupAttachmentsWifi);
    }, [ backupAttachmentsWifi ]);

    useEffect(() => {
        if (chooseResult) {
            setChooseResult(null);
        }
    }, [ chooseResult ]);

    useFocusEffect(useCallback(() => {
        reloadErrors();
    }, [ reloadErrors ]));

    useFocusEffect(useCallback(() => {
        fetchMetadataFiles(profileId);
        reloadErrors();
        getGoogleDiskInfo()
            .finally(() => getICloudInfo());
        fetchAllDataFiles()
            .catch(e => toLog('focus effect fetchAddDataFiles error', e));
    }, [ profileId ]));

    useFocusEffect(useCallback(() => {
        toLog('check finish iCloud restore attaches');
        finishICloudRestoreAttaches();
    }, []));

    //endregion

    //region Event handlers
    const onRefreshGoogleDriveHandler = useCallback(({ afterEnable = false } = {}) => {
        backupInfoProcessing.interactive = true;
        backupInfoProcessing.disableInteractive = false;
        reloadErrors();
        startGoogleBackup({
            onSuccess: () => {
                toLog('Google Drive backup started successfully, start get info');
                setTimeout(() => {
                    getGoogleDiskInfo();
                    reloadErrors();
                }, 1000);
            },
            onError: async (err, errCode) => {
                toLog(`Google Drive backup started error, start get info #${errCode}`, err);
                if (afterEnable) {
                    setGc(false);
                    GoogleSignin.signOut();
                    await dispatch(setGoogleCloudEnabled(false));
                }
                setTimeout(() => {
                    getGoogleDiskInfo();
                    reloadErrors();
                }, 1000);
            },
        });
    }, [ getGoogleDiskInfo, reloadErrors ]);

    const onRefreshICloudHandler = useCallback(() => {
        actions.clearLog();
        reloadErrors();
        startICloudBackup({
            onSuccess: (result) => {
                setTimeout(() => {
                    setBackupError('')
                        .finally(reloadErrors);
                    getICloudInfo();
                }, 3000);
                toLog('ICloud backup started successfully, start get info', result);
                reloadErrors();
            },
            onError: (err) => {
                toLog('ICloud backup started error, start get info', err);
                reloadErrors();
            },
        });
    }, [ reloadErrors ]);

    const gcChangeHandler = useCallback(
        async (value) => {
            if (!useGoogleBackup) {
                return;
            }
            if (value) {
                setBackupError('');
                console.log('Start get token for Google Drive');
                const token = await getDriveAccessToken(true, () => {
                    showNotification(Object.R('errors.googleDriveNoAccess'), false, true);
                });
                if (!token) {
                    showNotification(Object.R('errors.googleDriveNoAccess'), false, true);
                    return;
                }
                setGc(value);
                await dispatch(setGoogleCloudEnabled(value));
                backupInfoProcessing.disableInteractive = false;
                connectGoogleDrive({
                    onConnect: () => {
                        dispatch(setICloudEnabled(false));
                        setIc(false);
                        toLog('Google Drive connected, starting backup');
                        setTimeout(() => onRefreshGoogleDriveHandler({ afterEnable: true }), 0);
                    },
                    onCancel: () => {
                        toLog('Google Drive connection cancelled');
                        setBackupProcessing(false);
                    },
                    disableInteractive: true,
                })
                    .catch()
                    .then(() => {
                        // onRefreshGoogleDriveHandler();
                    });
            } else {
                setGc(value);
                await dispatch(setGoogleCloudEnabled(value));
            }
        },
        [ connectGoogleDrive,
            dispatch,
            useGoogleBackup ],
    );

    const icChangeHandler = useCallback(async (value) => {
        try {

            setIc(value);
            await dispatch(setICloudEnabled(value));
            if (value) {
                setBackupError('');
                dispatch(setGoogleCloudEnabled(false));
                setGc(false);
                backupInfoProcessing.disableInteractive = false;
                startICloudBackup({
                    onSuccess: () => {
                        toLog('ICloud connected, starting backup');
                        onRefreshICloudHandler();
                        setTimeout(reloadErrors, 0);
                    },
                    onError: (err) => {
                        toLog('ICloud connection error', err);
                        setBackupError(err?.message ?? '');
                        setTimeout(reloadErrors, 0);
                    },
                })
                    .catch(e => toLog('catch startICloudBackup', e));
            } else {
                setBackupError('');
                setTimeout(reloadErrors, 0);
            }
        } catch (e) {
            toLog('ERROR - ', e);
        }
    }, [ dispatch, onRefreshICloudHandler ]);

    const attachesChangeHandler = useCallback((value) => {
        setAttaches(value);
        dispatch(setBackupVisitAttachmentsEnabled(value));
    }, [ dispatch ]);

    const myDocsChangeHandler = useCallback((value) => {
        setMyDocs(value);
        dispatch(setBackupMyDocsEnabled(value));
    }, [ dispatch ]);

    const wifiChangeHandler = useCallback((value) => {
        setWifi(value);
        dispatch(setBackupAttachmentsWifiEnabled(value));
    }, [ dispatch ]);

    //endregion

    const clouds = useMemo(() => {
        const cloudNames = [];
        if (useGoogleBackup) {
            cloudNames.push(Object.R('titles.googleDrive'));
        }
        if (useICloudBackup) {
            cloudNames.push(Object.R('titles.iCloud'));
        }
        return cloudNames.join(', ');
    }, [ useGoogleBackup, useICloudBackup ]);

    const cloudUseText = useMemo(() => {
        const key = 'titles.useCloudText';
        const text = Object.R(key);
        return text === key ? '' : text;
    }, []);

    const doGoogleBackupRestore = useCallback((backup, rename = false) => {
        if (!backup) {
            toLog('No backup selected for restore');

        }
        toLog('Starting Google Drive backup restore', backup);
        restoreGoogleBackup(backup, rename)
            .then(() => {

                toLog('Google Drive backup restored successfully');
                showNotification(Object.R('notifications.restoreCompleted'));
                setRestoreError('');
                setTimeout(() => {
                    reloadErrors();
                }, 0);
            })
            .catch((error) => {
                setRestoreError(Object.R('errors.googleDriveRestoreFailed', { addInfo: ' ' + error?.message || '' }));
                showNotification(Object.R('warning.restoreFailed'), false, true);
                setTimeout(() => {
                    reloadErrors();
                }, 0);
                toLog('Error restoring Google Drive backup', error);
            });
    }, []);

    const onRestoreBackup = useCallback(
        (item) => {
            if (item.id === 1) {
                setRestoreError('');
                Object.backupCreated = true;
                getGoogleDriveBackupInfo(profileId, true)
                    .then((info) => {
                        const count = info?.backups?.length || 0;
                        if (count === 0) {
                            showNotification(Object.R('errors.googleDriveRestoreNoData'), false, true);
                            return;
                        }
                        // if (count === 1) {
                        //     doGoogleBackupRestore(info?.backups[0], false);
                        // } else if (count > 1) {
                        Page.showModal(<BackupList
                            list={info?.backups}
                            attachSize={info?.attachSize}
                            isIos={false}
                            onSelect={(i) => {
                                doGoogleBackupRestore(i, true);
                            }}/>);
                        // }
                    })
                    .catch(() => {
                        //
                    });
            }
            if (item.id === 2) {
                setRestoreError('');
                Object.backupCreated = true;
                toLog('Starting iCloud backup restore');
                getICloudBackupInfo(profileId)
                    .then((info) => {
                        toLog('iCloud backup info', info);
                        if (!info?.backups?.length) {
                            showNotification(Object.R('errors.iCloudRestoreNoData'), false, true);
                            return;
                        }
                        restoreICloud({
                            info,
                            alwaysChoose: true,
                            onComplete: () => {
                                showNotification(Object.R('notifications.restoreCompleted'));
                            },
                            onError: () => {
                                showNotification(Object.R('errors.restoreFailed'), false, true);
                            },
                        });
                    })
                    .catch((e) => {
                        toLog('error', 'Error get bbackup info from iCloud', e);
                        showNotification(Object.R('errors.iCloudRestoreNoData'), false, true);
                    });
            }
        },
        [ profileId, doGoogleBackupRestore ],
    );

    return (
        <Page
            isLoading={isInfoLoading || isICloudInfoProcessing || backupProcessing}
            safeArea
        >
            <TopNotification
                hint={Object.R('titles.backupScreenNotification', {
                    cloud: clouds,
                })}
            />
            {!!backupError && <Title
                style={{
                    color: COLORS.ERROR,
                    marginVertical: 16,
                    marginHorizontal: 16,
                }}
                numberOfLines={0}>{backupError}</Title>}
            {!!restoreError && <Title
                style={{
                    color: COLORS.ERROR,
                    marginVertical: 16,
                    marginHorizontal: 16,
                }}
                numberOfLines={0}>{restoreError}</Title>}
            <View style={[ styles.container ]}>
                <Section
                    title={cloudUseText}
                    style={styles.firstSectionStyle}
                >
                    {!!useICloudBackup && (
                        <>
                            <CloudBackupControl
                                enable={ic}
                                backupProcessing={backupProcessing}
                                infoProcessing={isInfoLoading || isICloudInfoProcessing}
                                isGoogleDrive={false}
                                isOpen={iCloudEnabled}
                                onRestore={() => onRestoreBackup({ id: 2 })}
                                backupInfo={iCloudLastBackup}
                                onBackup={onRefreshICloudHandler}
                                onChange={icChangeHandler}
                                infoLoading={isInfoLoading}
                            />
                            {/* <SwitchWithLabel */}
                            {/*     value={ic} */}
                            {/*     onValueChange={icChangeHandler} */}
                            {/*     caption={Object.R('buttons.icloudBackup')} */}
                            {/*     style={{ */}
                            {/*         marginTop: 0, */}
                            {/*     }} */}
                            {/*     enabled={!isICloudInfoProcessing || !isInfoLoading} */}
                            {/* /> */}
                            {/* <BackupInfo */}
                            {/*     backup={iCloudLastBackup} */}
                            {/*     enable={iCloudEnabled} */}
                            {/*     backupProcessing={backupProcessing} */}
                            {/*     infoProcessing={isInfoLoading} */}
                            {/*     isGoogleDrive={false} */}
                            {/*     onRefresh={onRefreshICloudHandler}/> */}
                        </>
                    )}
                    {(useGoogleRestore || useGoogleBackup) && <View style={{ paddingVertical: 5 }}/>}
                    {(!!useGoogleBackup || !!useGoogleRestore) && (<>
                        <CloudBackupControl
                            enable={gc}
                            backupProcessing={backupProcessing}
                            infoProcessing={isInfoLoading}
                            infoLoading={isInfoLoading}
                            isGoogleDrive={true}
                            isOpen={googleCloudEnabled}
                            onRestore={() => onRestoreBackup({ id: 1 })}
                            backupInfo={googleLastBackup}
                            onChange={gcChangeHandler}
                            onBackup={onRefreshGoogleDriveHandler}
                            isUseRestore={useGoogleRestore}
                            isUseBackup={useGoogleBackup}
                        />
                        {/* <SwitchWithLabel */}
                        {/*     value={gc} */}
                        {/*     onValueChange={gcChangeHandler} */}
                        {/*     caption={Object.R('buttons.googleBackup')} */}
                        {/*     style={{ */}
                        {/*         marginTop: 0, */}
                        {/*     }} */}
                        {/* /> */}
                    </>
                    )}
                    {/* {!!useGoogleBackup && ( */}
                    {/*     <> */}
                    {/*         <BackupInfo */}
                    {/*             backup={googleLastBackup} */}
                    {/*             enable={googleCloudEnabled} */}
                    {/*             backupProcessing={backupProcessing} */}
                    {/*             infoProcessing={isInfoLoading} */}
                    {/*             isGoogleDrive={true} */}
                    {/*             onRefresh={onRefreshGoogleDriveHandler}/> */}
                    {/*     </> */}
                    {/* )} */}
                </Section>
                <Section
                    title={Object.R('titles.backupAlsoReserved')}
                    style={styles.secondSectionStyle}
                >
                    {/* <SwitchWithLabel value={attaches} onValueChange={attachesChangeHandler} caption={Object.R('titles.backupVisitAttachmentsCheckbox')} />*/}
                    <CheckboxItem
                        name={Object.R('titles.backupVisitAttachmentsCheckbox')}
                        onSelect={attachesChangeHandler}
                        isSelected={attaches}
                        isDisabled={backupProcessing || isInfoLoading}
                        style={styles.checkbox}
                        containerStyle={[ styles.checkboxContainer, styles.checkboxContainerFirst ]}
                        checkboxStyle={{ marginLeft: 0 }}
                    />
                    {/* <SwitchWithLabel value={myDocs} onValueChange={myDocsChangeHandler} caption={Object.R('titles.backupVisitDocumentsCheckbox')} />*/}
                    <CheckboxItem
                        name={Object.R('titles.backupVisitDocumentsCheckbox')}
                        onSelect={myDocsChangeHandler}
                        isSelected={myDocs}
                        isDisabled={backupProcessing || isInfoLoading}
                        style={styles.checkbox}
                        containerStyle={styles.checkboxContainer}
                        checkboxStyle={{ marginLeft: 0 }}
                    />
                </Section>
                <Section
                    title={Object.R('titles.networkCondition')}
                    style={styles.secondSectionStyle}
                >
                    <SwitchWithLabel
                        value={wifi}
                        onValueChange={wifiChangeHandler}
                        caption={Object.R('titles.backupAttachmentsWifiCheckbox')}
                        style={{ marginTop: 0 }}
                    />
                </Section>
                {/* <NavigationList */}
                {/*     data={restoreList} */}
                {/*     style={styles.sectionStyle} */}
                {/*     onItem={onRestoreBackup} */}
                {/*     rightIcon={(item) => ( */}
                {/*         <Img */}
                {/*             source={item?.id === 1 ? gdrive : icloud} */}
                {/*             style={styles.image} */}
                {/*         /> */}
                {/*     )} */}
                {/*     disabled={isICloudInfoProcessing || isInfoLoading || backupProcessing} */}
                {/*     disabledStyle={styles.disabled} */}
                {/* /> */}
                <QAMode>
                    <>
                        <Button
                            title={'Sign out'}
                            action={() => GoogleSignin.signOut()}
                        />
                        <Log value={{
                            iCloudEnabled,
                            googleCloudEnabled,
                        }}/>
                    </>
                    <DebugButton hide={false}/>
                </QAMode>
            </View>
            <Modal
                visible={chooseVisible}
                transparent
                animationType="fade"
                onRequestClose={() => setChooseVisible(false)}
            >
                <View style={{
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                }}>
                </View>
            </Modal>
        </Page>
    );
});
